import { Config } from "@/client/types/Config";
import { create } from "zustand";
import { locales } from "@/client/utils/locales";

// A local edit. `features` is partial here because a caller patches one flag, not the whole set.
export type ConfigPatch = Partial<Omit<Config, "features">> & {
  features?: Partial<Config["features"]>;
};

interface ConfigState {
  config: Config;
  publicConfig: Config;
  companyConfig: Partial<Config>;
  setPublicConfig: (publicConfig: Config) => void;
  setCompanyConfig: (companyConfig: Partial<Config>) => void;
  patchConfig: (patch: ConfigPatch) => void;
  locales: { name: string; value: string }[];
}

const EMPTY_CONFIG = {
  theme: {},
  features: {},
  customCopy: {},
  backgroundInfo: {},
  froalaConfig: {},
  ticketingBrand: {},
} as Config;

// `features` is merged rather than replaced: the two payloads each carry a subset of the flags
// (the public one only what the signed-out shell reads), so a spread would drop the other's.
export const mergeConfigs = (base: Config, overlay: ConfigPatch): Config => ({
  ...base,
  ...overlay,
  features: { ...base.features, ...overlay.features },
});

export const useConfigStore = create<ConfigState>((set) => ({
  config: EMPTY_CONFIG,
  publicConfig: EMPTY_CONFIG,
  companyConfig: {},
  // One setter per fetcher, because either query can refetch mid-session and a merged write
  // would let whichever landed last drop the other's fields. Local edits use `patchConfig`.
  setPublicConfig: (publicConfig) =>
    set(({ companyConfig }) => ({
      publicConfig,
      config: mergeConfigs(publicConfig, companyConfig),
    })),
  setCompanyConfig: (companyConfig) =>
    set(({ publicConfig }) => ({
      companyConfig,
      config: mergeConfigs(publicConfig, companyConfig),
    })),
  patchConfig: (patch) =>
    set(({ publicConfig, companyConfig }) => {
      // Lands in the half merged last, so the edit wins whichever half owns the field.
      const patched = mergeConfigs(companyConfig as Config, patch);
      return { companyConfig: patched, config: mergeConfigs(publicConfig, patched) };
    }),
  locales,
}));
